import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Why Knee Health Matters After 60' },
  { id: 'knee-anatomy', label: 'What\'s Actually Happening in Your Knees' },
  { id: 'exercises', label: '10 Knee-Friendly Exercises' },
  { id: 'exercises-avoid', label: 'Exercises That Stress the Knees' },
  { id: 'progression', label: 'How to Progress Safely' },
  { id: 'sample-workout', label: 'A Complete Knee-Friendly Workout' },
  { id: 'mason-approach', label: "Mason's Approach to Knee-Safe Training" },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'joint-pain-exercises-for-seniors', title: 'Exercise WITH Joint Pain: Safe Movements for Men Over 60', category: 'Health & Recovery' },
  { slug: 'exercises-after-knee-replacement-over-60', title: 'Exercises After Knee Replacement: Getting Strong Again Over 60', category: 'Health & Recovery' },
  { slug: 'exercise-with-arthritis-over-60', title: 'Working Out with Arthritis Over 60: What You Can Still Do', category: 'Health Conditions' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '10 Knee-Friendly Exercises for Men Over 60',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-03-12',
    dateModified: '2024-04-06',
    description: '10 knee-friendly exercises for men over 60 that build strength without joint stress. Build powerful legs while protecting your knees for the long term.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/knee-friendly-exercises-for-seniors' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What exercises are easiest on the knees?',
        acceptedAnswer: { '@type': 'Answer', text: 'Swimming, cycling, and water aerobics are the easiest on the knees due to minimal impact and joint loading. For strength training, exercises like Romanian deadlifts, hip bridges, and seated leg press (limited range) build leg strength with minimal knee stress.' },
      },
      {
        '@type': 'Question',
        name: 'Can I build leg strength without hurting my knees?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Many effective leg exercises place minimal stress on the knee joint: Romanian deadlifts, hip bridges, hip abduction, calf raises, and partial-range squats. These build the muscles that support the knee without excessive joint loading.' },
      },
      {
        '@type': 'Question',
        name: 'Are squats bad for knees over 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Not necessarily. Partial-range squats (to 45-60 degrees) with proper form are generally safe and beneficial for knee health. Deep squats below 90 degrees increase knee joint stress significantly and should be avoided by men with knee pain or arthritis.' },
      },
      {
        '@type': 'Question',
        name: 'What is the best cardio for bad knees?',
        acceptedAnswer: { '@type': 'Answer', text: 'Swimming and water aerobics are the best cardio for bad knees — buoyancy eliminates most joint impact. Stationary cycling is also excellent. Walking on flat surfaces is generally well-tolerated. Avoid running, jumping, and stair climbing during flare-ups.' },
      },
    ],
  },
]);

export default function KneeFriendlyExercisesPage() {
  return (
    <BlogLayout
      title="10 Knee-Friendly Exercises for Men Over 60"
      description="10 knee-friendly exercises for men over 60 that build real strength without joint stress. Protect your knees while building powerful legs."
      keywords="knee-friendly exercises for seniors, knee-safe exercises men over 60, exercises for bad knees over 60, low impact leg exercises seniors"
      canonicalPath="/blog/knee-friendly-exercises-for-seniors"
      datePublished="2024-03-12"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="10 Knee-Friendly Exercises for Men Over 60" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health &amp; Recovery</span>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        10 Knee-Friendly Exercises for Men Over 60
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; March 12, 2024 &nbsp;·&nbsp; 9 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Why Knee Health Matters After 60</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The knees are the most commonly injured joints in men over 60. Osteoarthritis, previous injuries, and decades of wear all take their toll. But here's the thing — weak knees aren't inevitable, and knee pain doesn't have to stop you from training.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The solution isn't to avoid leg training. It's to choose exercises that build the muscles supporting the knee — quads, hamstrings, glutes, and calves — without placing excessive stress on the joint itself.
        </p>
        <p className="text-gray-300 leading-relaxed">
          These 10 exercises do exactly that. They're the foundation of knee-friendly training for men over 60.
        </p>
      </section>

      <section id="knee-anatomy" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">What's Actually Happening in Your Knees</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Understanding your knees helps you train them smarter. The knee joint is supported by four main muscle groups:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { muscle: 'Quadriceps', role: 'Front of thigh. Primary knee stabilizer. Weakness here is the #1 cause of knee pain in older men.' },
            { muscle: 'Hamstrings', role: 'Back of thigh. Balance the quads and protect the knee from hyperextension.' },
            { muscle: 'Glutes', role: 'Buttocks. Control hip alignment, which directly affects knee tracking and stress.' },
            { muscle: 'Calves', role: 'Lower leg. Absorb impact and support ankle-knee alignment.' },
          ].map((item) => (
            <div key={item.muscle} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-[#c8a96e] text-sm font-bold mb-1">{item.muscle}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.role}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-300 leading-relaxed">
          The <a href="https://www.mayoclinic.org/diseases-conditions/knee-pain/symptoms-causes/syc-20350849" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Mayo Clinic</a> confirms that strengthening the muscles around the knee is one of the most effective treatments for knee pain. Every exercise below targets one or more of these muscle groups.
        </p>
      </section>

      <section id="exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">10 Knee-Friendly Exercises</h2>
        <div className="space-y-4 mb-4">
          {[
            { num: 1, name: 'Hip Bridge', muscles: 'Glutes, hamstrings', sets: '3 sets × 15 reps', desc: 'Lie on your back, knees bent, feet flat. Drive hips up until body forms a straight line from shoulders to knees. Squeeze glutes at top. Zero knee stress, maximum glute activation.' },
            { num: 2, name: 'Romanian Deadlift', muscles: 'Hamstrings, glutes', sets: '3 sets × 10-12 reps', desc: 'Hold dumbbells, hinge at hips with slight knee bend, lower weights along legs until you feel hamstring stretch. Drive hips forward to return. Minimal knee involvement.' },
            { num: 3, name: 'Partial Squat (to 45°)', muscles: 'Quads, glutes', sets: '3 sets × 12-15 reps', desc: 'Squat to only 45 degrees — not full depth. This range builds quad strength with significantly less knee joint stress than deep squats.' },
            { num: 4, name: 'Seated Leg Extension', muscles: 'Quadriceps', sets: '3 sets × 15 reps', desc: 'Seated, extend one leg at a time against light resistance. Directly targets the quad without loading the knee joint under body weight.' },
            { num: 5, name: 'Straight Leg Raise', muscles: 'Quadriceps, hip flexors', sets: '3 sets × 12 reps each leg', desc: 'Lying flat, tighten quad and raise straight leg to 45 degrees. Builds quad strength with zero knee joint stress.' },
            { num: 6, name: 'Standing Hip Abduction', muscles: 'Glutes, hip abductors', sets: '3 sets × 15 reps each side', desc: 'Stand holding a support, raise one leg out to the side. Strengthens the glutes that control knee alignment.' },
            { num: 7, name: 'Calf Raise', muscles: 'Calves', sets: '3 sets × 20 reps', desc: 'Stand on edge of step, raise up on toes, lower slowly. Builds lower leg strength and ankle stability that supports the knee.' },
            { num: 8, name: 'Stationary Cycling', muscles: 'Quads, hamstrings, calves', sets: '20-30 minutes', desc: 'Adjust seat so knee has slight bend at bottom of pedal stroke. Excellent cardiovascular and leg conditioning with minimal knee impact.' },
            { num: 9, name: 'Wall Sit', muscles: 'Quads, glutes', sets: '3 × 30-60 seconds', desc: 'Back against wall, slide down to 90 degrees (or comfortable depth). Isometric quad strengthening with controlled joint position.' },
            { num: 10, name: 'Step-Up (low step)', muscles: 'Quads, glutes', sets: '3 sets × 10 reps each leg', desc: 'Use a 4-6 inch step. Step up, bring other foot up, step down. Functional strength for stairs with controlled knee loading.' },
          ].map((item) => (
            <div key={item.num} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <span className="w-8 h-8 flex-shrink-0 bg-[#c8a96e] text-black text-sm font-black rounded-full flex items-center justify-center">{item.num}</span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <strong className="text-white">{item.name}</strong>
                    <span className="text-[#c8a96e] text-xs font-bold">{item.sets}</span>
                    <span className="text-gray-500 text-xs">{item.muscles}</span>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercises That Stress the Knees</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Avoid or significantly modify these if you have knee pain:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Deep squats below 90 degrees — extreme knee joint compression',
            'Running and jumping — high impact forces through the knee',
            'Lunges with forward knee travel — significant patellar stress',
            'Leg press with excessive depth — same issue as deep squats',
            'Stair climbing with heavy loads',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="progression" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">How to Progress Safely</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Start conservatively and progress gradually. Here's the framework:
        </p>
        <div className="space-y-2 mb-4">
          {[
            { week: 'Weeks 1-2', focus: 'Master form with bodyweight or very light resistance. 2 sets per exercise.' },
            { week: 'Weeks 3-4', focus: 'Add light resistance. Increase to 3 sets. Monitor knee response.' },
            { week: 'Month 2', focus: 'Gradually increase weight when you can complete all reps with perfect form and no pain.' },
            { week: 'Month 3+', focus: 'Continue progressive overload. Add new exercises as strength and confidence build.' },
          ].map((item) => (
            <div key={item.week} className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-lg p-4">
              <span className="text-[#c8a96e] font-black text-sm w-24 flex-shrink-0">{item.week}</span>
              <p className="text-gray-400 text-sm">{item.focus}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="sample-workout" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">A Complete Knee-Friendly Workout</h2>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-4">Full Workout (3x per week)</p>
          <div className="space-y-2">
            {[
              { exercise: 'Warm-up walk', sets: '5 min' },
              { exercise: 'Hip Bridge', sets: '3 × 15' },
              { exercise: 'Partial Squat', sets: '3 × 12' },
              { exercise: 'Romanian Deadlift', sets: '3 × 10' },
              { exercise: 'Straight Leg Raise', sets: '3 × 12 each' },
              { exercise: 'Standing Hip Abduction', sets: '3 × 15 each' },
              { exercise: 'Calf Raise', sets: '3 × 20' },
              { exercise: 'Cool-down stretch', sets: '5 min' },
            ].map((item) => (
              <div key={item.exercise} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <span className="text-gray-300 text-sm">{item.exercise}</span>
                <span className="text-[#c8a96e] text-sm font-bold">{item.sets}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-gray-300 leading-relaxed">
          This workout takes about 35-40 minutes and hits every major muscle group that supports the knee. For a complete, progressive program built around these principles, the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> provides the full system.
        </p>
      </section>

      <section id="mason-approach" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Mason's Approach to Knee-Safe Training</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          At 60, my knees were a mess. Years of being overweight had taken their toll. I couldn't do a traditional squat without pain. I had to build a training system that worked around my limitations while still producing real results.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The exercises in this article are the foundation of that system. They're what I used to lose 55 lbs and get off 3 medications — all while protecting my knees throughout the process.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> is the complete, structured version of this approach — every exercise chosen specifically for men over 60 with joint considerations.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'What exercises are easiest on the knees?', a: 'Swimming, cycling, and water aerobics are easiest on the knees. For strength training: Romanian deadlifts, hip bridges, and seated leg press (limited range) build leg strength with minimal knee stress.' },
            { q: 'Can I build leg strength without hurting my knees?', a: 'Yes. Romanian deadlifts, hip bridges, hip abduction, calf raises, and partial-range squats build the muscles that support the knee without excessive joint loading.' },
            { q: 'Are squats bad for knees over 60?', a: 'Not necessarily. Partial-range squats (to 45-60 degrees) with proper form are generally safe. Deep squats below 90 degrees increase knee joint stress significantly and should be avoided by men with knee pain.' },
            { q: 'What is the best cardio for bad knees?', a: 'Swimming and water aerobics are best — buoyancy eliminates most joint impact. Stationary cycling is also excellent. Walking on flat surfaces is generally well-tolerated.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Every Exercise Is Knee-Safe by Design</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint is built around joint-safe movements like these. Mason used this exact approach to lose 55 lbs and get off 3 medications at age 62.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
